import { useRef, useEffect } from 'react';
import { TextInput } from '@ardoq/forms';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { Box } from '@ardoq/layout';
import {
  percentageStoredValueToEditValue,
  percentageEditValueToStoredValue,
} from '@ardoq/locale';
import { NUMBER_FORMAT_PERCENTAGE } from '@ardoq/api-types';
import { CellEditor } from './types';

/**
 * Custom number cell editor that handles percentage formatting.
 * For percentage fields, converts stored fraction (0.25) to percentage (25) for editing,
 * then converts back when saving.
 */
export const NumberCellEditor: CellEditor<number> = props => {
  const { value, context, colDef, onValueChange } = props;
  const field = enhancedScopeDataOperations.getFieldByName(
    context.scopeData,
    colDef.field!
  );
  const formatOption = field?.numberFormatOptions;
  const isPercentage = formatOption === NUMBER_FORMAT_PERCENTAGE;

  // Convert stored value (0.25) to edit value (25) for percentages
  const editValue =
    isPercentage && value !== null && value !== undefined
      ? percentageStoredValueToEditValue(value)
      : value;

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleValueChange = (newValue: string) => {
    const numericValue = parseFloat(newValue);
    if (isNaN(numericValue) || newValue === '') {
      onValueChange(null);
      return;
    }
    // Convert edit value (25) back to stored value (0.25) for percentages
    const storedValue = isPercentage
      ? percentageEditValueToStoredValue(numericValue)
      : numericValue;
    onValueChange(storedValue);
  };

  return (
    <Box width="full">
      <TextInput
        ref={inputRef}
        value={
          editValue !== null && editValue !== undefined ? String(editValue) : ''
        }
        onValueChange={handleValueChange}
        type="number"
      />
    </Box>
  );
};
